import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { Mail, Lock, User, Building2, Loader2, AlertCircle, Check, X, Gift } from 'lucide-react';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    company: ''
  });
  const [error, setError] = useState('');
  const [emailStatus, setEmailStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const { signup, validateEmail } = useAuth();
  const navigate = useNavigate();

  // Debounced email validation
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (formData.email && formData.email.includes('@')) {
        setCheckingEmail(true);
        try {
          const result = await validateEmail(formData.email);
          setEmailStatus(result);
        } catch (err) {
          setEmailStatus({ valid: false, message: 'Error validating email' });
        } finally {
          setCheckingEmail(false);
        }
      } else {
        setEmailStatus(null);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.email]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validations
    if (!emailStatus?.valid) {
      setError(emailStatus?.message || 'Please enter a valid work email');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await signup(formData.email, formData.password, formData.fullName, formData.company);
      navigate('/dashboard?trial=started', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <section className="pt-40 pb-20 px-6">
        <div className="max-w-md mx-auto">
          <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-full px-4 py-2 mb-4">
                <Gift className="w-4 h-4 text-green-400" />
                <span className="text-green-400 font-medium text-sm">14-Day Free Trial</span>
              </div>
              <h1 className="text-3xl font-bold text-[#F8FAFC] mb-2">Start Your Free Trial</h1>
              <p className="text-[#94A3B8]">No credit card required. Work email only.</p>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#F8FAFC] mb-2">Work Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full bg-[#0A0F1C] border rounded-lg py-3 pl-10 pr-10 text-[#F8FAFC] placeholder-[#94A3B8] focus:outline-none ${
                      emailStatus === null ? 'border-[#2563EB]/30 focus:border-[#2563EB]' :
                      emailStatus.valid ? 'border-green-500' : 'border-red-500'
                    }`}
                    placeholder="you@company.com"
                    required
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {checkingEmail && <Loader2 className="w-5 h-5 text-[#94A3B8] animate-spin" />}
                    {!checkingEmail && emailStatus?.valid && <Check className="w-5 h-5 text-green-400" />}
                    {!checkingEmail && emailStatus && !emailStatus.valid && <X className="w-5 h-5 text-red-400" />}
                  </div>
                </div>
                {emailStatus && !emailStatus.valid && (
                  <p className="text-red-400 text-xs mt-1">{emailStatus.message}</p>
                )}
                <p className="text-[#94A3B8] text-xs mt-1">Free email providers (Gmail, Yahoo, etc.) are not allowed</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F8FAFC] mb-2">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="w-full bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg py-3 pl-10 pr-4 text-[#F8FAFC] placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB]"
                    placeholder="John Doe"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F8FAFC] mb-2">Company</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg py-3 pl-10 pr-4 text-[#F8FAFC] placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB]"
                    placeholder="Acme Inc."
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F8FAFC] mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg py-3 pl-10 pr-4 text-[#F8FAFC] placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB]"
                    placeholder="Min. 8 characters"
                    required
                    minLength={8}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F8FAFC] mb-2">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full bg-[#0A0F1C] border rounded-lg py-3 pl-10 pr-4 text-[#F8FAFC] placeholder-[#94A3B8] focus:outline-none ${
                      formData.confirmPassword && formData.password !== formData.confirmPassword
                        ? 'border-red-500'
                        : 'border-[#2563EB]/30 focus:border-[#2563EB]'
                    }`}
                    placeholder="Confirm password"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || !emailStatus?.valid}
                className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white py-4 rounded-lg font-semibold disabled:opacity-50"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creating account...</>
                ) : (
                  <><Gift className="w-5 h-5 mr-2" /> Start 14-Day Free Trial</>
                )}
              </Button>
            </form>

            <p className="text-center text-[#94A3B8] mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-[#2563EB] hover:underline font-medium">
                Sign in
              </Link>
            </p>

            <div className="mt-6 pt-6 border-t border-[#2563EB]/20">
              <p className="text-xs text-[#94A3B8] text-center">
                Your trial includes: 10 SKUs, Sweetspot, Price Hub, 1 User
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SignupPage;